import { BlockBlobClient } from '@azure/storage-blob';
import { GoFormzServices } from '../../AppSettings';
import { apimCall } from '../../helpers/api';
import { UserPersistedState, UserPersistedStateDispatch, UserPersistedStateUpdate } from './userpersistedstate-types';
import userStateDefaults from './userpersistedstate-defaults';
import moment from 'moment';
import { RootState } from 'gf-types';

export const GET_USER_PERSISTED_STATE_SUCCESS = 'GET_USER_PERSISTED_STATE_SUCCESS';
export const GET_USER_PERSISTED_STATE_FAILURE = 'GET_USER_PERSISTED_STATE_FAILURE';
export const SET_USER_PERSISTED_STATE_SUCCESS = 'SET_USER_PERSISTED_STATE_SUCCESS';
export const GET_USER_PERSISTED_STATE_BLOB_NOT_FOUND_FAILURE = 'GET_USER_PERSISTED_STATE_BLOB_NOT_FOUND_FAILURE';
const browserStorageKey = 'clientid:userpersistedstate:userid';

const addStorageListener = (dispatch: UserPersistedStateDispatch) => {
  window.addEventListener('storage', (ev: StorageEvent) => {
    if (ev.key !== browserStorageKey) {
      return;
    }

    const stateFromStorage = JSON.parse(localStorage.getItem(browserStorageKey) ?? '{}');
    dispatch({
      type: GET_USER_PERSISTED_STATE_SUCCESS,
      payload: stateFromStorage
    });
  });
};

export function getUserPersistedState() {
  return async function (dispatch: UserPersistedStateDispatch) {
    try {
      const blobUrl = await apimCall(GoFormzServices.AccountService, `/profile/userpersistedstate`, 'GET');
      const blobClient = new BlockBlobClient(blobUrl.data);

      const json = await blobClient.download();
      const jsonBlob = await json.blobBody;
      const jsonText = await jsonBlob.text();
      const state = JSON.parse(jsonText) as UserPersistedState;

      const stateWithDefaults = { ...userStateDefaults, ...state };
      addStorageListener(dispatch);

      dispatch({
        type: GET_USER_PERSISTED_STATE_SUCCESS,
        payload: stateWithDefaults
      });
    } catch (err) {
      console.error(err);
      if (err.response && err.response.status === 404) {
        dispatch({ type: GET_USER_PERSISTED_STATE_BLOB_NOT_FOUND_FAILURE, payload: userStateDefaults });
      } else {
        dispatch({ type: GET_USER_PERSISTED_STATE_FAILURE });
      }
    }
  };
}

export function setUserPersistedState(update: UserPersistedStateUpdate) {
  return async function (dispatch: UserPersistedStateDispatch, getState: () => RootState) {
    try {
      const currentState = getState()?.userPersistedState ?? {};
      const fullUpdate = { ...currentState, ...(update ?? {}) };
      const stateJson = JSON.stringify(fullUpdate ?? {});
      const stateJsonBlobLength = new Blob([stateJson]).size;

      //update browser storage to support cross tabs
      localStorage.setItem(browserStorageKey, stateJson);

      dispatch({
        type: SET_USER_PERSISTED_STATE_SUCCESS,
        payload: fullUpdate
      });

      //upload to server for any new requests
      getUserPersistedStateBlobClient()
        .then((blobClient) => blobClient.upload(stateJson, stateJsonBlobLength))
        .catch((e) => console.log(e));
    } catch (err) {
      console.error(err);
    }
  };
}

const UserPersistedBlobState = {
  blobUrl: null
};

const getUserPersistedStateBlobClient = async (refreshSasToken?: boolean) => {
  let sasExpired = false;
  if (UserPersistedBlobState.blobUrl != null) {
    const decodedUri = decodeURIComponent(UserPersistedBlobState.blobUrl);
    const urlParams = new URLSearchParams(decodedUri);
    const expirationDate = urlParams.get('se');
    const currentTimeUtc = moment().utc().toISOString();

    if (currentTimeUtc > expirationDate) {
      sasExpired = true;
    }
  }

  if (UserPersistedBlobState.blobUrl == null || refreshSasToken || sasExpired) {
    const blobUrlResponse = await apimCall(GoFormzServices.AccountService, `/profile/userpersistedstate`, 'GET');
    if (blobUrlResponse.status != 200) {
      console.error('Failed To Get UserPersistedState BlobUrl', blobUrlResponse);
      return null;
    }
    UserPersistedBlobState.blobUrl = blobUrlResponse.data;
  }

  const blobClient = new BlockBlobClient(UserPersistedBlobState.blobUrl);
  return blobClient;
};
